import React, { useContext, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Eye,
  Pencil,
  PackagePlus,
  Trash2,
  ArrowDownToLine,
  ArrowUpFromLine,
} from "lucide-react";
import InnerLayout from "../../../components/layouts/inner-layout";
import DynamicTable from "../../../components/tables/dynamic-table1";
import LoadingSpinner from "../../../components/helper-components/loading-spinner";
import AccessDenied from "../../../components/helper-components/access-denied";
import emptyInvoice from "../../../assets/images/empty-invoice.png";
import AddButton from "../../../components/buttons/button";
import { DataContext } from "../../../components/layouts/main-layout";
import successToast from "../../../components/helper-components/success-toast";
import errorToast from "../../../components/helper-components/error-toast";
import {
  warehouseBinListAction,
  warehouseBinDeleteAction,
  warehouseBinSetDefaultAction,
} from "../../../redux/actions/inventory-action";
import GenerateBinsModal from "./modals/generate-bins-modal";
import AddBinModal from "./modals/add-bin-modal";
import EditBinModal from "./modals/edit-bin-modal";
import AddDefaultItemsModal from "./modals/add-default-items-modal";
import BinItemsSidePanel from "./modals/bin-items-side-panel";

const Badge = ({ text, danger }) => (
  <span
    style={{
      width: "fit-content",
      backgroundColor: danger
        ? "var(--danger-color-opacity)"
        : "var(--success-color-opacity)",
    }}
    className={`fs_12 ${
      danger ? "text-F51D00" : "text-027A48"
    } fw_500 flex items-center gap-2 px-3 py-1 rounded-full`}
  >
    {text}
  </span>
);

// Sellable, Non-Sellable and Expired are independent flags — a bin can be
// any combination of the three at once (e.g. expired but still marked
// sellable) — so every applicable badge/filter is shown together rather
// than picking a single "status".
const STATUS_FIELD = {
  sellable: "is_sellable_bin_location",
  non_sellable: "is_non_sellable_bin_location",
  expired: "is_expired_bin_location",
};

const STATUS_LABELS = {
  sellable: "Sellable",
  non_sellable: "Non-Sellable",
  expired: "Expired",
};

const StatusBadges = (bin) => {
  const badges = Object.keys(STATUS_FIELD)
    .filter((key) => bin?.[STATUS_FIELD[key]])
    .map((key) => ({ key, danger: key !== "sellable" }));
  return (
    <div className="flex flex-wrap items-center gap-2">
      {badges.length === 0 && <span className="text-667085 fs_12">--</span>}
      {badges.map((badge) => (
        <Badge
          key={badge.key}
          text={STATUS_LABELS[badge.key]}
          danger={badge.danger}
        />
      ))}
      {bin?.default_receiving_bin && (
        <Badge text="Default Receiving" danger={false} />
      )}
      {bin?.default_dropping_bin && (
        <Badge text="Default Dropping" danger={false} />
      )}
    </div>
  );
};

const UserInfo = ({ name }) => (
  <span className={name ? "text-000000" : "text-667085"}>{name ?? "--"}</span>
);

const WarehouseBinManagement = () => {
  const { warehouseId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { permissionFn } = useContext(DataContext);
  const hasReadAccess = permissionFn("warehouse_info")?.read === true;

  const [bins, setBins] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    sellable: 0,
    non_sellable: 0,
    expired: 0,
  });
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterData, setFilterData] = useState({
    sort_by: "",
    sort_order: "",
    selectColumn: "",
    from: "",
    to: "",
    inputValue: "",
  });

  const [statusFilters, setStatusFilters] = useState([]);

  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [addBinModalOpen, setAddBinModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [defaultItemsModalOpen, setDefaultItemsModalOpen] = useState(false);
  const [binItemsPanelOpen, setBinItemsPanelOpen] = useState(false);
  const [selectedBin, setSelectedBin] = useState(null);

  // The search column only affects results when there's search text, so
  // switching it with an empty search box shouldn't trigger a reload.
  const searchColumn = filterData?.inputValue ? filterData?.selectColumn : "";

  useEffect(() => {
    if (!hasReadAccess || !warehouseId) return;
    setLoading(true);
    dispatch(
      warehouseBinListAction(
        {
          warehouse_id: warehouseId,
          pageNumber: currentPage,
          pageSize,
          search: filterData?.inputValue,
          selectColumn: searchColumn,
          statusFilters,
        },
        (res) => {
          setBins(res?.data?.bins || []);
          setTotalCount(res?.data?.totalCount || 0);
          setStatusCounts(
            res?.data?.statusCounts || { all: 0, sellable: 0, non_sellable: 0, expired: 0 }
          );
          setLoading(false);
        },
        () => {
          setBins([]);
          setTotalCount(0);
          setStatusCounts({ all: 0, sellable: 0, non_sellable: 0, expired: 0 });
          setLoading(false);
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasReadAccess,
    warehouseId,
    currentPage,
    pageSize,
    filterData?.inputValue,
    searchColumn,
    statusFilters,
    reload,
  ]);

  const toggleStatusFilter = (key) => {
    setStatusFilters((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
    setCurrentPage(1);
  };

  const handleDeleteBin = (e, bin_no_id) => {
    e.stopPropagation();
    setLoading(true);
    dispatch(
      warehouseBinDeleteAction(
        { bin_no_id },
        () => {
          successToast("Bin deleted successfully");
          setReload((prev) => !prev);
        },
        (data) => {
          setLoading(false);
          errorToast(data?.message);
        }
      )
    );
  };

  const handleSetDefault = (e, bin, field) => {
    e.stopPropagation();
    if (bin?.[field]) return;
    setLoading(true);
    dispatch(
      warehouseBinSetDefaultAction(
        {
          warehouse_id: warehouseId,
          bin_no_id: bin?.bin_no_id,
          field,
        },
        () => {
          successToast("Default bin updated successfully");
          setReload((prev) => !prev);
        },
        (data) => {
          setLoading(false);
          errorToast(data?.message);
        }
      )
    );
  };

  const tableBodyData = bins?.map((bin) => ({
    id: bin?.bin_no_id,
    bin_location_no: bin?.bin_location_no ?? "--",
    bin_priority: bin?.bin_priority ?? "--",
    max_quantity_display: bin?.max_quantity ?? "--",
    max_weight_display: bin?.max_weight ?? "--",
    current_qty_display: bin?.current_qty ?? 0,
    status_display: StatusBadges(bin),
    created_by_display: <UserInfo name={bin?.created_by_name} />,
    updated_by_display: <UserInfo name={bin?.updated_by_name} />,
    bin_actions: (
      <div
        className="flex items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          title="View bin items"
          onClick={() => {
            setSelectedBin(bin);
            setBinItemsPanelOpen(true);
          }}
        >
          <Eye size={16} />
        </button>
        <button
          type="button"
          title="Edit bin"
          onClick={() => {
            setSelectedBin(bin);
            setEditModalOpen(true);
          }}
        >
          <Pencil size={16} />
        </button>
        <button
          type="button"
          title="View / manage default items"
          onClick={() => {
            setSelectedBin(bin);
            setDefaultItemsModalOpen(true);
          }}
        >
          <PackagePlus size={16} />
        </button>
        <button
          type="button"
          disabled={!!bin?.default_receiving_bin}
          title={
            bin?.default_receiving_bin
              ? "Already the default receiving bin"
              : "Set as default receiving bin"
          }
          className={bin?.default_receiving_bin ? "cursor-not-allowed" : ""}
          onClick={(e) => handleSetDefault(e, bin, "default_receiving_bin")}
        >
          <ArrowDownToLine
            size={16}
            className={bin?.default_receiving_bin ? "text-027A48" : ""}
          />
        </button>
        <button
          type="button"
          disabled={!!bin?.default_dropping_bin}
          title={
            bin?.default_dropping_bin
              ? "Already the default dropping bin"
              : "Set as default dropping bin"
          }
          className={bin?.default_dropping_bin ? "cursor-not-allowed" : ""}
          onClick={(e) => handleSetDefault(e, bin, "default_dropping_bin")}
        >
          <ArrowUpFromLine
            size={16}
            className={bin?.default_dropping_bin ? "text-027A48" : ""}
          />
        </button>
        <button
          type="button"
          title="Delete bin"
          onClick={(e) => handleDeleteBin(e, bin?.bin_no_id)}
        >
          <Trash2 size={16} className="text-F94B4B" />
        </button>
      </div>
    ),
  }));

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount: totalCount,
    setCurrentPage,
    currentPage,
  };

  const summaryArray = (
    <div className="px-[24px] pt-[16px] flex items-center gap-3 flex-wrap">
      <AddButton
        text="Generate Bins"
        prefixIcon={false}
        className="border border-primary text-primary px-4 py-2 rounded-md flex items-center gap-2 fw_500 text-sm"
        onclkFn={() => setGenerateModalOpen(true)}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => {
            setStatusFilters([]);
            setCurrentPage(1);
          }}
          className={`fs_12 fw_500 px-3 py-1.5 rounded-full border whitespace-nowrap ${
            statusFilters.length === 0
              ? "border-primary text-[#fff] bg-primary"
              : "border-787878 text-000000"
          }`}
        >
          All ({statusCounts.all})
        </button>
        {Object.keys(STATUS_LABELS).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => toggleStatusFilter(key)}
            className={`fs_12 fw_500 px-3 py-1.5 rounded-full border whitespace-nowrap ${
              statusFilters.includes(key)
                ? "border-primary text-[#fff] bg-primary"
                : "border-787878 text-000000"
            }`}
          >
            {STATUS_LABELS[key]} ({statusCounts[key]})
          </button>
        ))}
      </div>
    </div>
  );

  const title = location?.state?.warehouse_name
    ? `Warehouse Bins — ${location.state.warehouse_name}`
    : "Warehouse Bins";

  if (!hasReadAccess) {
    return (
      <InnerLayout title={title} backBtn={true}>
        <AccessDenied message="You don't have permission to view Warehouse Info." />
      </InnerLayout>
    );
  }

  return (
    <InnerLayout
      list={true}
      onclkFn={() => setAddBinModalOpen(true)}
      title={title}
      module="warehouse_info"
      permissionFn={permissionFn}
      backBtn={true}
    >
      {loading ? (
        <LoadingSpinner />
      ) : statusCounts.all === 0 &&
        !filterData?.inputValue &&
        statusFilters.length === 0 ? (
        <div className="flex flex-col justify-center items-center h-full">
          <div>
            <img src={emptyInvoice} alt="empty-invoice" />
            <h3 className="flex justify-center text-000000 fw_600 fs_18">
              No bins found{" "}
            </h3>
            <p className="flex justify-center text-667085 fw_400 mt-4 text-sm">
              Generate bins in bulk or add a single bin to get started.{" "}
            </p>
            <div className="flex justify-center gap-3 mt-3">
              <AddButton
                text="Generate Bins"
                prefixIcon={false}
                className="border border-primary text-primary px-4 py-2 rounded-md flex items-center gap-2 fw_500 text-sm"
                onclkFn={() => setGenerateModalOpen(true)}
              />
              {permissionFn("warehouse_info")?.create && (
                <AddButton
                  text="Add Bin"
                  onclkFn={() => setAddBinModalOpen(true)}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <DynamicTable
          is_checkbox_req={false}
          id_name="bin_no_id"
          list={true}
          onclkFn={() => setAddBinModalOpen(true)}
          filterSec={true}
          filter={false}
          date={false}
          permissionFn={permissionFn}
          module="warehouse_info"
          onClickItemFn={() => {}}
          reload={reload}
          setReload={setReload}
          loading={loading}
          setLoading={setLoading}
          label="Bin"
          paginationProps={paginationProps}
          filterData={filterData}
          setFilterData={setFilterData}
          tableHeading={tableHeading}
          tableBodyData={tableBodyData}
          placeholder="Search"
          newBtnTxt="Add Bin"
          summaryArray={summaryArray}
          initialFilter={{
            sort_by: "",
            sort_order: "",
            selectColumn: "",
            from: "",
            to: "",
            inputValue: "",
          }}
        />
      )}

      <GenerateBinsModal
        isModalOpen={generateModalOpen}
        setIsModalOpen={setGenerateModalOpen}
        warehouseId={warehouseId}
        onGenerated={() => setReload((prev) => !prev)}
      />
      <AddBinModal
        isModalOpen={addBinModalOpen}
        setIsModalOpen={setAddBinModalOpen}
        warehouseId={warehouseId}
        onAdded={() => setReload((prev) => !prev)}
      />
      <EditBinModal
        isModalOpen={editModalOpen}
        setIsModalOpen={setEditModalOpen}
        bin={selectedBin}
        onUpdated={() => setReload((prev) => !prev)}
      />
      <AddDefaultItemsModal
        isModalOpen={defaultItemsModalOpen}
        setIsModalOpen={setDefaultItemsModalOpen}
        bin={selectedBin}
        onAdded={() => setReload((prev) => !prev)}
      />
      <BinItemsSidePanel
        open={binItemsPanelOpen}
        setOpen={setBinItemsPanelOpen}
        bin={selectedBin}
      />
    </InnerLayout>
  );
};

export default WarehouseBinManagement;

const tableHeading = [
  {
    label: "BIN LOCATION NO",
    value: "bin_location_no",
    display: false,
    column: true,
  },
  {
    label: "PRIORITY",
    value: "bin_priority",
    display: false,
    column: true,
  },
  {
    label: "MAX QTY",
    value: "max_quantity_display",
    display: false,
    column: false,
  },
  {
    label: "MAX WEIGHT",
    value: "max_weight_display",
    display: false,
    column: false,
  },
  {
    label: "CURRENT QTY",
    value: "current_qty_display",
    display: false,
    column: false,
  },
  {
    label: "STATUS",
    value: "status_display",
    display: false,
    column: false,
  },
  {
    label: "CREATED BY",
    value: "created_by_display",
    display: false,
    column: false,
  },
  {
    label: "UPDATED BY",
    value: "updated_by_display",
    display: false,
    column: false,
  },
  {
    label: "BIN ACTIONS",
    value: "bin_actions",
    display: false,
    column: false,
  },
];
